// @ts-check

/** @file Lexical attachment node for uploaded-file-backed editor files. */

import {$applyNodeReplacement, createCommand, DecoratorNode} from "lexical"

import {attachmentMetadataText, parseSizeBytes} from "./attachment-metadata"
import {editorAttachmentAttribute, pendingUploadAttribute, uploadedFileAttribute} from "./upload-session"

/** @import {EditorConfig, LexicalCommand, LexicalNode, NodeKey, SerializedLexicalNode} from "lexical" */
/** @import {ReactElement} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{contentType: string, filename: string, pendingUploadId?: string, sizeBytes?: number | null, uploadedFileId?: string}} AttachmentNodePayload */
/** @typedef {SerializedLexicalNode & AttachmentNodePayload & {version: 1}} SerializedAttachmentNode */

/** @type {LexicalCommand<AttachmentNodePayload>} */
export const INSERT_ATTACHMENT_COMMAND = createCommand("INSERT_ATTACHMENT_COMMAND")

/** Lexical node for non-image files embedded in WYSIWYG fields. */
export class AttachmentNode extends DecoratorNode {
  /** @type {string} */
  __contentType
  /** @type {string} */
  __filename
  /** @type {string | undefined} */
  __pendingUploadId
  /** @type {number | null | undefined} */
  __sizeBytes
  /** @type {string | undefined} */
  __uploadedFileId

  /**
   * Stores editor attachment metadata.
   * @param {AttachmentNodePayload} payload Upload metadata.
   * @param {NodeKey} [key] Existing Lexical node key when cloning.
   */
  constructor({contentType, filename, pendingUploadId, sizeBytes, uploadedFileId}, key) {
    super(key)
    this.__contentType = contentType
    this.__filename = filename
    this.__pendingUploadId = pendingUploadId
    this.__sizeBytes = sizeBytes
    this.__uploadedFileId = uploadedFileId
  }

  /**
   * Identifies this node in serialized Lexical state.
   * @returns {string} Lexical node type.
   */
  static getType() {
    return "attachment"
  }

  /**
   * Clones an attachment node.
   * @param {AttachmentNode} node Existing node.
   * @returns {AttachmentNode} Cloned node.
   */
  static clone(node) {
    return new AttachmentNode({
      contentType: node.__contentType,
      filename: node.__filename,
      pendingUploadId: node.__pendingUploadId,
      sizeBytes: node.__sizeBytes,
      uploadedFileId: node.__uploadedFileId
    }, node.__key)
  }

  /**
   * Imports serialized attachment state.
   * @param {SerializedAttachmentNode} serializedNode Payload produced by exportJSON.
   * @returns {AttachmentNode} Imported attachment node.
   */
  static importJSON(serializedNode) {
    return $createAttachmentNode({
      contentType: serializedNode.contentType,
      filename: serializedNode.filename,
      pendingUploadId: serializedNode.pendingUploadId,
      sizeBytes: serializedNode.sizeBytes,
      uploadedFileId: serializedNode.uploadedFileId
    })
  }

  /**
   * Imports persisted editor attachment tags.
   * @returns {Record<string, () => {conversion: (element: HTMLElement) => {node: LexicalNode | null}, priority: 1}>} DOM conversion map.
   */
  static importDOM() {
    return {
      div: () => ({
        conversion: convertAttachmentElement,
        priority: 1
      })
    }
  }

  /**
   * Exports serialized attachment state.
   * @returns {SerializedAttachmentNode} Serialized node.
   */
  exportJSON() {
    return {
      contentType: this.__contentType,
      filename: this.__filename,
      pendingUploadId: this.__pendingUploadId,
      sizeBytes: this.__sizeBytes,
      type: "attachment",
      uploadedFileId: this.__uploadedFileId,
      version: 1
    }
  }

  /**
   * Creates the wrapper DOM node.
   * @returns {HTMLElement} Wrapper element.
   */
  createDOM() {
    return document.createElement("div")
  }

  /**
   * Keeps the same wrapper DOM node.
   * @returns {false} Lexical should not replace the wrapper.
   */
  updateDOM() {
    return false
  }

  /**
   * Marks this node as block content.
   * @returns {false} Attachments are block-level cards.
   */
  isInline() {
    return false
  }

  /**
   * Exports the attachment as a metadata-bearing HTML `div`.
   * @returns {{element: HTMLDivElement}} Exported DOM node.
   */
  exportDOM() {
    const element = document.createElement("div")

    element.setAttribute(editorAttachmentAttribute, "true")
    element.setAttribute("data-content-type", this.__contentType)
    element.setAttribute("data-file-name", this.__filename)
    if (this.__pendingUploadId) {
      element.setAttribute(pendingUploadAttribute, this.__pendingUploadId)
    }
    if (typeof this.__sizeBytes === "number") {
      element.setAttribute("data-file-size", String(this.__sizeBytes))
    }
    if (this.__uploadedFileId) {
      element.setAttribute(uploadedFileAttribute, this.__uploadedFileId)
    }
    element.textContent = this.__filename

    return {element}
  }

  /**
   * Renders the attachment card inside the editor.
   * @returns {ReactElement} Rendered attachment card.
   */
  decorate() {
    const metadata = attachmentMetadataText({
      contentType: this.__contentType,
      sizeBytes: this.__sizeBytes
    })

    return (
      <div
        contentEditable={false}
        data-content-type={this.__contentType}
        data-editor-attachment="true"
        data-file-name={this.__filename}
        data-file-size={typeof this.__sizeBytes === "number" ? String(this.__sizeBytes) : undefined}
        data-pending-upload-id={this.__pendingUploadId}
        data-uploaded-file-id={this.__uploadedFileId}
        style={styles.card ||= {
          backgroundColor: "#f8fafc",
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          boxSizing: "border-box",
          color: "#0f172a",
          display: "block",
          marginBottom: 8,
          maxWidth: "100%",
          padding: 10
        }}
      >
        <span
          style={styles.filename ||= {
            display: "block",
            fontSize: 14,
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {this.__filename}
        </span>
        {metadata &&
          <span
            style={styles.metadata ||= {
              color: "#475569",
              display: "block",
              fontSize: 12,
              marginTop: 4
            }}
          >
            {metadata}
          </span>
        }
      </div>
    )
  }
}

/**
 * Creates an attachment node.
 * @param {AttachmentNodePayload} payload Upload metadata.
 * @returns {AttachmentNode} Replacement attachment node.
 */
export function $createAttachmentNode(payload) {
  return $applyNodeReplacement(new AttachmentNode(payload))
}

/**
 * Converts an HTML attachment element to a Lexical attachment node.
 * @param {HTMLElement} element DOM element.
 * @returns {{node: LexicalNode | null}} Converted node.
 */
function convertAttachmentElement(element) {
  if (element.getAttribute(editorAttachmentAttribute) !== "true") {
    return {node: null}
  }

  const filename = element.getAttribute("data-file-name") || element.textContent?.trim()

  if (!filename) {
    return {node: null}
  }

  return {
    node: $createAttachmentNode({
      contentType: element.getAttribute("data-content-type") || "application/octet-stream",
      filename,
      pendingUploadId: element.getAttribute(pendingUploadAttribute) || undefined,
      sizeBytes: parseSizeBytes(element.getAttribute("data-file-size")),
      uploadedFileId: element.getAttribute(uploadedFileAttribute) || undefined
    })
  }
}
